import React, { useContext, useState } from "react";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";

import * as Yup from "yup";
import Flex from "../../../../components/shared-styles/flexi";
import Input from "../../../../components/form/input"; 
import InputArea from "../../../../components/form/inputArea";
import DatePicker from "../../../../components/form/DatePicker";
import { formatErrors, handleInput, handleSubmit } from "../../../../services/utils/processors";
import { ToastContext } from "../../../../services/contexts/toast-context";
import styled from "styled-components";

// -- Styled Components for Redesign --
const CardContainer = styled.div`
  min-width: 50vw;
  background-color: var(--surface-card, #ffffff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    min-width: 100%;
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--surface-border, #f3f4f6);
  padding-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--text-color, #374151);
    font-weight: 600;
  }
`;

const GridLine = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Projects = ({ defaultData = [], loading = false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const [projects, setProjects] = useState(Array.isArray(defaultData) ? formatDateFields(defaultData) : []);
  const [projectError, setProjectError] = useState({});

  const schema = Yup.array().of(
    Yup.object({
      title: Yup.string().required("Project title is required"),
      description: Yup.string().required("Description is required"),
      skills: Yup.array().of(Yup.string()),
      start_date: Yup.date().nullable(),
      end_date: Yup.date().nullable(),
    })
  );

  function formatDateFields(items) {
    return items.map(item => ({
      ...item,
      skills: Array.isArray(item.skills) ? item.skills : [],
      start_date: item.start_date ? (String(item.start_date).length === 4 ? new Date(`${item.start_date}-01-01`) : new Date(item.start_date)) : null,
      end_date: item.end_date ? (String(item.end_date).length === 4 ? new Date(`${item.end_date}-12-31`) : new Date(item.end_date)) : null,
    }));
  }

  const handleInputChange = (value, index, field) => {
    const newProjects = [...projects];
    newProjects[index][field] = value;
    setProjects(newProjects);
  };

  const addProject = () => {
    setProjects([
      ...projects,
      { title: "", description: "", skills: [], start_date: null, end_date: null },
    ]);
  };

  const removeProject = (index) => {
    const newProjects = projects.filter((_, i) => i !== index);
    setProjects(newProjects);
  };

  const handleSubmitData = () => {
    handleSubmit(projects, schema, setProjectError, onSave);
    if (projectError && Object.entries(projectError).length !== 0) {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: formatErrors(projectError),
        life: 6000
      });
    }
    setProjectError({});
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <Button 
          label="Add Project" 
          icon="pi pi-plus" 
          outlined
          className="p-button-secondary" 
          onClick={addProject} 
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
        {projects.map((project, index) => (
          <CardContainer key={index}>
            <CardHeader>
              <h3>{project.title ? project.title : `Project ${index + 1}`}</h3>
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => removeProject(index)}
                aria-label="Remove Project"
                style={{ padding: "8px" }}
              />
            </CardHeader>
            <Flex direction="column" alignItems="stretch" gap="20px" style={{width: "100%"}}>
              
              <div>
                <Input
                  value={project.title}
                  label="Project Title"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "title")}
                  error={projectError?.[index]?.title}
                />
              </div>

              <GridLine>
                <DatePicker
                  value={project.start_date}
                  label="Start Date"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "start_date")}
                  error={projectError?.[index]?.start_date}
                />
                <DatePicker
                  value={project.end_date}
                  label="End Date"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "end_date")}
                  error={projectError?.[index]?.end_date}
                />
              </GridLine>

              <div style={{ width: "100%" }}>
                <InputArea
                  value={project.description}
                  label="Description"
                  width="100%"
                  height="120px"
                  onChange={(value) => handleInputChange(value, index, "description")}
                  error={projectError?.[index]?.description}
                />
              </div>

              <div style={{ width: "100%" }}>
                <label className="font-bold">Skills used in this project</label>
                <Chips
                  value={project.skills || []}
                  onChange={(event) => handleInputChange(event.value, index, "skills")}
                  placeholder="Type a skill and press Enter"
                  style={{ width: "100%", marginTop: "6px" }}
                />
              </div>

            </Flex>
          </CardContainer>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
        <Button 
          label="Save & Continue" 
          icon="pi pi-check" 
          loading={loading} 
          disabled={loading} 
          className="p-button-primary" 
          onClick={handleSubmitData} 
        />
      </div>
    </div>
  );
};

export default Projects;
